/**
 * Cognito error -> copy a person can act on.
 *
 * Both auth forms used to render `error.message` verbatim, so a returning user
 * who mistyped a password saw "Incorrect username or password." and a returning
 * user on the signup form saw "User already exists" — accurate, but neither one
 * tells you what to do next, and the one thing that would (a link to the other
 * form) was never offered. `action` is that next step, rendered by the form as a
 * link beside the message.
 */

export type AuthErrorAction = "login" | "signup" | "reset";

export interface AuthError {
  message: string;
  action?: AuthErrorAction;
}

/** Cognito exposes the exception type as `name`; `code` on older SDK builds. */
function errorName(error: unknown): string {
  if (typeof error !== "object" || error === null) return "";
  const e = error as { name?: unknown; code?: unknown };
  if (typeof e.name === "string" && e.name) return e.name;
  if (typeof e.code === "string") return e.code;
  return "";
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "";
}

/** Cognito's minimum is 8 characters, no character-class requirements. */
export const PASSWORD_HINT = "At least 8 characters.";

/** Cognito wraps a pre-signup Lambda rejection as
 *  "PreSignUp failed with error <the Lambda's message>." */
function unwrapLambdaMessage(raw: string): string {
  const m = /failed with error (.+)$/i.exec(raw);
  const inner = (m?.[1] ?? "").trim();
  if (!inner) return "We could not create an account with that email address.";
  // Cognito appends its own "." to a message that usually already ends in one, so the
  // wrapper yields "…address..". Collapse the run and guarantee exactly one.
  return inner.replace(/\.+$/, "") + ".";
}

export function signupError(error: unknown): AuthError {
  const name = errorName(error);
  const raw = errorMessage(error);

  switch (name) {
  case "UsernameExistsException":
    return {
      message: "An account with this email already exists.",
      action: "login",
    };
  case "InvalidPasswordException":
    return { message: `That password doesn't meet the requirements. ${PASSWORD_HINT}` };
  case "InvalidParameterException":
    // Covers both a malformed email and a too-short password; the raw text
    // distinguishes them and is specific enough to show.
    return {
      message: /password/i.test(raw)
        ? `That password doesn't meet the requirements. ${PASSWORD_HINT}`
        : "Please enter a valid email address.",
    };
  case "TooManyRequestsException":
  case "LimitExceededException":
    return { message: "Too many attempts. Please wait a minute and try again." };
  case "UserLambdaValidationException":
    // The Cognito pre-signup Lambda rejected it: a disposable or email-to-SMS
    // domain, a gmail dot-trick address, or a failed Turnstile check.
    // Cognito wraps the Lambda's own message in "PreSignUp failed with error <msg>."
    // — unwrap it, or the visitor reads our internals.
    return { message: unwrapLambdaMessage(raw) };
  default:
    return { message: raw || "Sign up failed. Please try again." };
  }
}

/** Login hit an account that signed up but never entered its emailed code —
 *  the login form switches to the confirm step instead of showing an error. */
export function isUserNotConfirmed(error: unknown): boolean {
  return errorName(error) === "UserNotConfirmedException";
}

/** Cognito's answer to confirming (or resending for) an account that is
 *  already confirmed — e.g. a second tab, or a double-submitted code. Not a
 *  failure: the caller should just sign in. */
export function isAlreadyConfirmed(error: unknown): boolean {
  const name = errorName(error);
  const raw = errorMessage(error);
  return (
    (name === "NotAuthorizedException" && /status is CONFIRMED/i.test(raw)) ||
    (name === "InvalidParameterException" && /already confirmed/i.test(raw))
  );
}

export function confirmError(error: unknown): AuthError {
  const name = errorName(error);
  const raw = errorMessage(error);

  switch (name) {
  case "CodeMismatchException":
    return { message: "That code doesn't match. Check the email and try again." };
  case "ExpiredCodeException":
    return { message: "That code has expired. Send a new one below." };
  case "TooManyRequestsException":
  case "LimitExceededException":
  case "TooManyFailedAttemptsException":
    return { message: "Too many attempts. Please wait a few minutes and try again." };
  default:
    return { message: raw || "We couldn't confirm your account. Please try again." };
  }
}

export function loginError(error: unknown): AuthError {
  const name = errorName(error);
  const raw = errorMessage(error);

  switch (name) {
  case "NotAuthorizedException":
    // A disabled account (bot triage) fails with the same exception as a wrong
    // password; telling a real person to reset a password that isn't the
    // problem sends them in a loop.
    if (/disabled/i.test(raw)) {
      return {
        message:
          "This account has been disabled. If you think that's a mistake, email contact@dejachart.com.",
      };
    }
    return { message: "Incorrect email or password.", action: "reset" };
  case "UserNotFoundException":
    return { message: "We couldn't find an account with that email.", action: "signup" };
  case "UserNotConfirmedException":
    return { message: "This account isn't confirmed yet. Enter the code we emailed you." };
  case "PasswordResetRequiredException":
    return { message: "You need to reset your password before logging in.", action: "reset" };
  case "TooManyRequestsException":
  case "LimitExceededException":
    return { message: "Too many attempts. Please wait a minute and try again." };
  default:
    return { message: raw || "Log in failed. Please try again." };
  }
}
